import { useEffect, useMemo } from 'react'

import type { VuiComponent } from '../core'
import type { TagProps } from './tag.types'

import { styled, useStyleConfig, vui } from '../core'
import { DismissButton } from '../dismissButton'
import Icon from '../icon'
import T from '../t'
import { fontSizes } from '../theme'
import { cs, filterUndefined, isReactText, isString } from '../utils'
import { resolveTagVariantKey } from './utils'

// ---------------------------------------------------------------------------
// Styled primitives
// ---------------------------------------------------------------------------

/** Outer container — rendered as <span> for static tags and <button> for interactive tags. */
const TagBase = styled.spanBox`
  /* Drives DismissButton color via cascade so the close affordance picks up the tag's text color. */
  --vui-dismiss-fg: currentColor;

  align-items: center;
  display: inline-flex;
  font-weight: medium;
  justify-content: center;
  line-height: 1;
  max-width: 100%;
  position: relative;
  transition-duration: fast;
  width: fit-content;

  &:focus {
    outline: none;
  }
`

// ---------------------------------------------------------------------------
// Tag component
// ---------------------------------------------------------------------------

/**
 * Displays text, icons or custom content. Can be made interactive or include a
 * dismiss affordance. When `isInteractive` (or `onClick` without `onDelete`)
 * is set, the tag renders as a `<button>` so it is keyboard-activatable.
 */
export const Tag = vui<'span', TagProps>((props, ref) => {
  const {
    children,
    className,
    deleteLabel = 'Remove',
    icon,
    startIcon,
    endIcon,
    iconLeft,
    iconRight,
    color: colorProp,
    isInteractive = props.onClick !== undefined && props.onDelete === undefined,
    isTruncated = true,
    itemLeft,
    itemRight,
    onClick,
    onDelete,
    size = 'md',
    text,
    variant: variantProp,
    ...rest
  } = props

  // Resolve canonical theme key from the two-prop API (variant + color).
  const resolvedVariant = useMemo(() => resolveTagVariantKey(variantProp, colorProp), [variantProp, colorProp])

  // Dev-mode deprecation warning for legacy combined variants.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      if (iconLeft !== undefined) {
        console.warn('[Tag] `iconLeft` is deprecated. Use `startIcon` instead.')
      }
      if (iconRight !== undefined) {
        console.warn('[Tag] `iconRight` is deprecated. Use `endIcon` instead.')
      }
      if (
        variantProp !== undefined &&
        !['subtle', 'solid'].includes(variantProp) &&
        /^(subtle|solid)(Brand|Success|Danger|Warning|Lavender|Eucalyptus|Terracotta|Grey)$/.test(variantProp)
      ) {
        console.warn(
          `[Tag] Combined variant "${variantProp}" is deprecated. ` +
            'Use separate `variant` + `color` props instead (e.g. variant="subtle" color="blue").',
        )
      }
    }
  }, [iconLeft, iconRight, variantProp])

  const resolvedStartIcon = startIcon ?? iconLeft
  const resolvedEndIcon = endIcon ?? iconRight

  const styles = useStyleConfig('Tag', { ...props, variant: resolvedVariant })
  const {
    h,
    fontSize: themeFontSize,
    paddingX = 0,
    paddingY = 0,
    gapX = 0,
    borderRadius,
    bgHover,
    bgPressed,
    ...containerColors
  } = styles.container ?? {}
  const dismissStyles = styles.dismiss ?? {}
  const iconStyles = styles.icon ?? {}

  const resolvedFontSize = fontSizes[themeFontSize as keyof typeof fontSizes] ?? themeFontSize
  const fontSize = `var(--vui-tag-font-size, ${resolvedFontSize}px)`

  const resolvedIconSize = iconStyles.fontSize || '1rem'
  const iconSize = `var(--vui-tag-icon-size, ${resolvedIconSize})`

  const TagIcon = ({ name, mr, ml }: { name: string; mr?: string; ml?: string }) => (
    <Icon className="vui-tagIcon" fontSize={iconSize} h={iconSize} ml={ml} mr={mr} w={iconSize} name={name} />
  )

  // Theme numeric values are raw pixel measurements (mirrors button/theme.ts).
  // Convert to px strings to bypass xstyled's `spaces` scale lookup on padding/gap props.
  const toPx = (v: unknown) => (typeof v === 'number' ? `${v}px` : v)
  const pxX = toPx(paddingX)
  const pxY = toPx(paddingY)
  const pxGap = toPx(gapX)

  const aliasedProps = filterUndefined({
    borderRadius,
  })

  const interactiveStateProps = isInteractive
    ? filterUndefined({
        hoverBg: bgHover,
        activeBg: bgPressed,
      })
    : undefined

  const interactiveProps = isInteractive
    ? {
        as: 'button' as const,
        type: 'button' as const,
        cursor: 'pointer',
        tabIndex: 0,
        userSelect: 'none',
      }
    : {}

  return (
    <TagBase
      className={cs('vui-tag', className)}
      focusRing={2}
      fontSize={fontSize}
      h={h}
      isTruncated={isTruncated}
      onClick={onClick}
      px={pxX}
      py={pxY}
      ref={ref}
      variant={resolvedVariant}
      {...containerColors}
      {...interactiveProps}
      {...interactiveStateProps}
      {...aliasedProps}
      {...rest}
    >
      {isString(icon) ? <TagIcon name={icon} /> : icon}

      {!icon && (
        <>
          {itemLeft}

          {isString(resolvedStartIcon) ? <TagIcon name={resolvedStartIcon} mr={pxGap as string} /> : resolvedStartIcon}

          {children ??
            (isReactText(text) ? (
              <T className="vui-tagText" fontSize="inherit" isTruncated={isTruncated}>
                {text}
              </T>
            ) : (
              text
            ))}

          {isString(resolvedEndIcon) ? <TagIcon name={resolvedEndIcon} ml={pxGap as string} /> : resolvedEndIcon}

          {onDelete && !isInteractive ? (
            <DismissButton
              aria-label={deleteLabel}
              className="vui-tagButton"
              ml={pxGap}
              onClick={onDelete}
              {...dismissStyles}
            />
          ) : (
            itemRight
          )}
        </>
      )}
    </TagBase>
  )
}) as VuiComponent<'span', TagProps>

Tag.displayName = 'Tag'

export default Tag
